import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Card } from "antd";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/Component";
import { Icon } from "../../components/Component";
import { useDispatch, useSelector } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import TopMenu from "../../route/topMenu";
import { expensesApis } from "../../api/Expenses";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Table, Spin } from "antd";
import type { ColumnsType } from "antd/lib/table";
import SkeletonTable from "../../utils/skeleton-templates/table";
import type { SkeletonTableColumnsType } from "../../utils/skeleton-templates/table";
import dayjs from "dayjs";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

// Interface definitions
interface AnalyticsSummary {
  totalExpenses: number;
  thisMonth: number;
  lastMonth: number;
  growthRate: number;
}

interface CategoryData {
  category: string;
  amount: number;
  percentage: number;
  color: string;
}

interface ProjectData {
  project: string;
  amount: number;
  percentage: number;
}

interface MonthlyData {
  month: string;
  amount: number;
  trend: string;
}

interface UserData {
  user: string;
  amount: number;
  percentage: number;
}

const ExpensesAnalytics: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ projectId: string }>();
  const projectIdFromUrl = params.projectId;
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectId, setProjectId] = useState<string | number | null>(null);
  
  // Analytics data states
  const [summary, setSummary] = useState<AnalyticsSummary>({
    totalExpenses: 0,
    thisMonth: 0,
    lastMonth: 0,
    growthRate: 0,
  });
  const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
  const [projectData, setProjectData] = useState<ProjectData[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [userData, setUserData] = useState<UserData[]>([]);

  useEffect(() => {
    // Initialize menu data for expenses pages
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location, dispatch]);

  useEffect(() => {
    const checkMobileView = () => {
      setMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  useEffect(() => {
    sm
      ? document.body.classList.add("toggle-shown")
      : document.body.classList.remove("toggle-shown");
  }, [sm]);

  // Extract projectId from URL params first, then location state
  useEffect(() => {
    const { id: projectIdFromState, projectId: projectIdAlt } = location.state || {};
    const resolvedProjectId = projectIdFromUrl || projectIdFromState || projectIdAlt;
    if (resolvedProjectId && !isNaN(Number(resolvedProjectId))) {
      setProjectId(resolvedProjectId);
    }
  }, [location.state, projectIdFromUrl]);

  // Fetch analytics data using existing APIs
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (projectId) {
        setLoading(true);
        try {
          const isAdmin = currentUser?.genericRole?.toLowerCase() === "admin";
          const expensesParams = !isAdmin ? { userId: currentUser?.id || currentUser?.userId } : {};
          
          // Fetch expenses and reports data using existing APIs
          const [expensesResponse, reportsResponse] = await Promise.all([
            expensesApis.fetchExpenses(projectId, expensesParams),
            expensesApis.fetchReports(projectId),
          ]);

          // Process expenses data for analytics
          if (expensesResponse?.status && expensesResponse.data) {
            const expenses = Array.isArray(expensesResponse.data) ? expensesResponse.data : [];
            
            // Calculate summary statistics
            const totalExpenses = expenses.reduce((sum: number, expense: any) => sum + (expense.amount || 0), 0);
            const thisMonth = dayjs().format('YYYY-MM');
            const lastMonth = dayjs().subtract(1, 'month').format('YYYY-MM');
            
            const thisMonthExpenses = expenses
              .filter((expense: any) => dayjs(expense.expenseDate).format('YYYY-MM') === thisMonth)
              .reduce((sum: number, expense: any) => sum + (expense.amount || 0), 0);
            
            const lastMonthExpenses = expenses
              .filter((expense: any) => dayjs(expense.expenseDate).format('YYYY-MM') === lastMonth)
              .reduce((sum: number, expense: any) => sum + (expense.amount || 0), 0);
            
            const growthRate = lastMonthExpenses > 0 ? ((thisMonthExpenses - lastMonthExpenses) / lastMonthExpenses) * 100 : 0;

            setSummary({
              totalExpenses,
              thisMonth: thisMonthExpenses,
              lastMonth: lastMonthExpenses,
              growthRate,
            });

          // Process category data
            const categoryMap = new Map();
            expenses.forEach((expense: any) => {
              const category = expense.category || 'Uncategorized';
              const amount = expense.amount || 0;
              if (categoryMap.has(category)) {
                categoryMap.set(category, categoryMap.get(category) + amount);
              } else {
                categoryMap.set(category, amount);
              }
            });

            const colors = ["#1890ff", "#52c41a", "#faad14", "#f5222d", "#722ed1", "#13c2c2", "#eb2f96", "#fa8c16"];
            const processedCategoryData = Array.from(categoryMap.entries()).map(([category, amount], index) => ({
              category,
              amount,
              percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0,
              color: colors[index % colors.length],
            }));
            setCategoryData(processedCategoryData);

            // Process user data
            const userMap = new Map();
            expenses.forEach((expense: any) => {
              const user = expense.createdByName || expense.createdBy;
              const amount = expense.amount || 0;
              if (userMap.has(user)) {
                userMap.set(user, userMap.get(user) + amount);
              } else {
                userMap.set(user, amount);
              }
            });

            const processedUserData = Array.from(userMap.entries()).map(([user, amount]) => ({
              user,
              amount,
              percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0,
            }));
            setUserData(processedUserData);

            // Process monthly data (last 6 months)
            const monthlyMap = new Map<string, number>();
            const last6Months: string[] = [];
            for (let i = 5; i >= 0; i--) {
              const month = dayjs().subtract(i, 'month').format('YYYY-MM');
              last6Months.push(month);
              monthlyMap.set(month, 0);
            }

            expenses.forEach((expense: any) => {
              const month = dayjs(expense.expenseDate).format('YYYY-MM');
              if (monthlyMap.has(month)) {
                monthlyMap.set(month, monthlyMap.get(month) + (expense.amount || 0));
              }
            });

            const processedMonthlyData = last6Months.map((month, index) => {
              const amount = monthlyMap.get(month) || 0;
              const prevAmount = index > 0 ? monthlyMap.get(last6Months[index - 1]) || 0 : 0;
              const trend = prevAmount > 0 ? ((amount - prevAmount) / prevAmount) * 100 : 0;
              return {
                month: dayjs(month).format('MMM YYYY'),
                amount,
                trend: `${trend >= 0 ? '+' : ''}${trend.toFixed(1)}%`,
              };
            });
            setMonthlyData(processedMonthlyData);
          }

          // Process project data (if reports are available)
          if (reportsResponse?.status && reportsResponse.data) {
            const reports = Array.isArray(reportsResponse.data) ? reportsResponse.data : [];
            const projectMap = new Map();
            
            reports.forEach((report: any) => {
              const project = report.projectName || 'Current Project';
              const amount = report.totalAmount || 0;
              if (projectMap.has(project)) {
                projectMap.set(project, projectMap.get(project) + amount);
              } else {
                projectMap.set(project, amount);
              }
            });

            const totalProjectAmount = Array.from(projectMap.values()).reduce((sum, amount) => sum + amount, 0);
            const processedProjectData = Array.from(projectMap.entries()).map(([project, amount]) => ({
              project,
              amount,
              percentage: totalProjectAmount > 0 ? (amount / totalProjectAmount) * 100 : 0,
            }));
            setProjectData(processedProjectData);
          }

        } catch (error) {
          // Set fallback data on error
          setSummary({
            totalExpenses: 0,
            thisMonth: 0,
            lastMonth: 0,
            growthRate: 0,
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAnalyticsData();
  }, [projectId]);

  // Chart configurations
  const categoryChartData = {
    labels: categoryData.map(item => item.category),
    datasets: [
      {
        label: 'Amount (₹)',
        data: categoryData.map(item => item.amount),
        backgroundColor: categoryData.map(item => item.color),
        borderColor: categoryData.map(item => item.color),
        borderWidth: 1,
      },
    ],
  };

  const projectChartData = {
    labels: projectData.map(item => item.project),
    datasets: [
      {
        label: 'Amount (₹)',
        data: projectData.map(item => item.amount),
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const monthlyChartData = {
    labels: monthlyData.map(item => item.month),
    datasets: [
      {
        label: 'Amount (₹)',
        data: monthlyData.map(item => item.amount),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const userChartData = {
    labels: userData.map(item => item.user),
    datasets: [
      {
        label: 'Amount (₹)',
        data: userData.map(item => item.amount),
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            return value >= 1000 ? (value / 1000) + 'K' : value;
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  // Table columns
  const categoryColumns: ColumnsType<CategoryData> = [
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 200,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: (amount: number) => `₹${amount.toLocaleString()}`,
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      width: 120,
      render: (percentage: number, record: CategoryData) => (
        <div>
          <div className="progress" style={{ height: '8px' }}>
            <div 
              className="progress-bar" 
              style={{ 
                width: `${percentage}%`,
                backgroundColor: record.color
              }}
            ></div>
          </div>
          <small className="text-muted">{percentage}%</small>
        </div>
      ),
    },
  ];

  const projectColumns: ColumnsType<ProjectData> = [
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      width: 200,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: (amount: number) => `₹${amount.toLocaleString()}`,
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      width: 120,
      render: (percentage: number) => (
        <div>
          <div className="progress" style={{ height: '8px' }}>
            <div 
              className="progress-bar bg-success" 
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
          <small className="text-muted">{percentage}%</small>
        </div>
      ),
    },
  ];

  const monthlyColumns: ColumnsType<MonthlyData> = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 100,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: (amount: number) => `₹${amount.toLocaleString()}`,
    },
    {
      title: "Trend",
      dataIndex: "trend",
      key: "trend",
      width: 100,
      render: (trend: string) => (
        <span className={`badge ${trend.startsWith('+') ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '8px', padding: '2px 6px', fontFamily: 'Inter, Roboto, sans-serif' }}>
          {trend}
        </span>
      ),
    },
  ];

  const userColumns: ColumnsType<UserData> = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 200,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: (amount: number) => `₹${amount.toLocaleString()}`,
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      width: 120,
      render: (percentage: number) => (
        <div>
          <div className="progress" style={{ height: '8px' }}>
            <div 
              className="progress-bar bg-info" 
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
          <small className="text-muted">{percentage}%</small>
        </div>
      ),
    },
  ];

  return (
    <>
      <Head title="Expenses Analytics" />
      <Content>
        <Card className="card-bordered">
            <div className="card-inner card-inner-lg">
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">Expenses Analytics</BlockTitle>
                  </BlockHeadContent>
                  <BlockHeadContent>
                    <div className="toggle-wrap nk-block-tools-toggle">
                      <button
                        className="btn btn-icon btn-trigger toggle-expand me-n1"
                        onClick={() => setSm(!sm)}
                      >
                        <Icon name="menu-alt-r"></Icon>
                      </button>
                      <div className="toggle-expand-content">
                        <ul className="nk-block-tools g-3">
                          <li className="nk-block-tools-opt">
                            <button
                              className="btn btn-outline-light"
                              onClick={() => navigate(-1)}
                              style={{ borderRadius: "6px", height: "32px", fontSize: '12px', padding: '4px 12px', fontWeight: "500", fontFamily: 'Inter, Roboto, sans-serif' }}
                            >
                              <Icon name="arrow-left" className="me-1" size="sm" />
                              Back to Expenses
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Block>
                <div style={{ marginBottom: "16px" }}>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px', fontFamily: 'Inter, Roboto, sans-serif' }}>
                    Comprehensive analysis of your expense data
                  </p>
                </div>

                {/* Summary Statistics */}
                <div className="row g-3 mb-4">
                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card">
                      <div className="card-body text-center">
                        <div className="text-muted small mb-1">Total Expenses</div>
                        <div className="h4 text-primary mb-0">₹{summary.totalExpenses.toLocaleString()}</div>
                        <Icon name="dollar-sign" className="text-primary" />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card">
                      <div className="card-body text-center">
                        <div className="text-muted small mb-1">This Month</div>
                        <div className="h4 text-info mb-0">₹{summary.thisMonth.toLocaleString()}</div>
                        <Icon name="trending-up" className="text-info" />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card">
                      <div className="card-body text-center">
                        <div className="text-muted small mb-1">Last Month</div>
                        <div className="h4 text-warning mb-0">₹{summary.lastMonth.toLocaleString()}</div>
                        <Icon name="trending-down" className="text-warning" />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card">
                      <div className="card-body text-center">
                        <div className="text-muted small mb-1">Growth Rate</div>
                        <div className={`h4 mb-0 ${summary.growthRate >= 0 ? 'text-success' : 'text-danger'}`}>
                          {summary.growthRate >= 0 ? '+' : ''}{summary.growthRate.toFixed(1)}%
                        </div>
                        <Icon name="percent" className={summary.growthRate >= 0 ? 'text-success' : 'text-danger'} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts and Tables */}
                <div className="row g-3">
                  <div className="col-12 col-lg-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="card-title mb-0">Expenses by Category</h6>
                        <Icon name="pie-chart" className="text-primary" />
                      </div>
                      <div className="card-body">
                        {loading ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "300px",
                            }}
                          >
                            <Spin size="large" tip="Loading analytics data..." />
                          </div>
                        ) : categoryData.length > 0 ? (
                          <>
                            <div style={{ height: "300px", marginBottom: "20px" }}>
                              <Doughnut data={categoryChartData} options={doughnutOptions} />
                            </div>
                            <div className="table-responsive">
                              <SkeletonTable
                                loading={loading}
                                columns={categoryColumns as SkeletonTableColumnsType[]}
                              >
                                <Table
                                  rowKey="category"
                                  columns={categoryColumns}
                                  dataSource={categoryData}
                                  pagination={false}
                                  size="small"
                                  bordered
                                />
                              </SkeletonTable>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-4 text-muted">
                            No category data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="card-title mb-0">Expenses by Project</h6>
                        <Icon name="bar-chart" className="text-primary" />
                      </div>
                      <div className="card-body">
                        {loading ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "300px",
                            }}
                          >
                            <Spin size="large" tip="Loading analytics data..." />
                          </div>
                        ) : projectData.length > 0 ? (
                          <>
                            <div style={{ height: "300px", marginBottom: "20px" }}>
                              <Bar data={projectChartData} options={chartOptions} />
                            </div>
                            <div className="table-responsive">
                              <SkeletonTable
                                loading={loading}
                                columns={projectColumns as SkeletonTableColumnsType[]}
                              >
                                <Table
                                  rowKey="project"
                                  columns={projectColumns}
                                  dataSource={projectData}
                                  pagination={false}
                                  size="small"
                                  bordered
                                />
                              </SkeletonTable>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-4 text-muted">
                            No project data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mt-3">
                  <div className="col-12 col-lg-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="card-title mb-0">Monthly Trend</h6>
                        <Icon name="line-chart" className="text-primary" />
                      </div>
                      <div className="card-body">
                        {loading ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "300px",
                            }}
                          >
                            <Spin size="large" tip="Loading analytics data..." />
                          </div>
                        ) : monthlyData.length > 0 ? (
                          <>
                            <div style={{ height: "300px", marginBottom: "20px" }}>
                              <Line data={monthlyChartData} options={chartOptions} />
                            </div>
                            <div className="table-responsive">
                              <SkeletonTable
                                loading={loading}
                                columns={monthlyColumns as SkeletonTableColumnsType[]}
                              >
                                <Table
                                  rowKey="month"
                                  columns={monthlyColumns}
                                  dataSource={monthlyData}
                                  pagination={false}
                                  size="small"
                                  bordered
                                />
                              </SkeletonTable>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-4 text-muted">
                            No monthly data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="card-title mb-0">Expenses by User</h6>
                        <Icon name="users" className="text-primary" />
                      </div>
                      <div className="card-body">
                        {loading ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "300px",
                            }}
                          >
                            <Spin size="large" tip="Loading analytics data..." />
                          </div>
                        ) : userData.length > 0 ? (
                          <>
                            <div style={{ height: "300px", marginBottom: "20px" }}>
                              <Bar data={userChartData} options={chartOptions} />
                            </div>
                            <div className="table-responsive">
                              <SkeletonTable
                                loading={loading}
                                columns={userColumns as SkeletonTableColumnsType[]}
                              >
                                <Table
                                  rowKey="user"
                                  columns={userColumns}
                                  dataSource={userData}
                                  pagination={false}
                                  size="small"
                                  bordered
                                />
                              </SkeletonTable>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-4 text-muted">
                            No user data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Block>
            </div>
        </Card>
      </Content>
    </>
  );
};

export default ExpensesAnalytics;
